import {
  formatPairingCode,
  PAIRING_TTL_MINUTES,
  type PairingRequestSummary,
  type ServerDetail,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import {
  approvePairingRequest,
  denyPairingRequest,
  listPairingRequests,
  lookupPairingCode,
} from '../api/pairing.js';
import { isApiError } from '../api/client.js';
import { listServerGroups } from '../api/servers.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField, TextField } from '../ui/Field.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * Pairing (knowledge doc section 10, method B).
 *
 * The screen exists for one act: comparing four things on a terminal against four
 * things here, and then saying yes. So the details are what the page is mostly
 * made of, and approving is deliberately a second step rather than a button on the
 * waiting list — the list shows that a machine is asking, and the code from *that*
 * machine's terminal is what says it is the one you meant.
 *
 * The code lives in component state and nowhere else. Not the URL, not storage, not
 * the query cache key: it is a secret with fifteen minutes to live, and a URL is
 * written to history and to every log between here and the API.
 */

const NO_GROUP = '__none__';

interface Found {
  request: PairingRequestSummary;
  /** Kept so approving can present it again, which is what binds the two steps. */
  code: string;
}

export function PairingPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const queryClient = useQueryClient();

  const [found, setFound] = useState<Found | null>(null);
  const [admitted, setAdmitted] = useState<ServerDetail | null>(null);

  const waiting = useQuery({ queryKey: ['pairing-requests'], queryFn: listPairingRequests });

  const afterChange = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['pairing-requests'] });
    void queryClient.invalidateQueries({ queryKey: ['servers'] });
  };

  return (
    <div className="page">
      <PageHeader
        title="Pairing"
        description={
          <>
            How one server joins without an enrollment token. Run <code>bartizo-agent pair</code> on
            the machine, then type the code it prints here.
          </>
        }
      />

      {admitted !== null && (
        <Alert tone="success" title={`${admitted.hostname} has joined`}>
          <p className="text-sm">
            It is active and should send its first heartbeat within a minute.{' '}
            <Link to={`/servers/${admitted.id}`}>Open {admitted.name}</Link>
          </p>
        </Alert>
      )}

      <EnterCodeCard
        onFound={(next) => {
          setAdmitted(null);
          setFound(next);
        }}
      />

      {found !== null && (
        <CheckAndApproveCard
          found={found}
          timezone={timezone}
          onCancel={() => {
            setFound(null);
          }}
          onApproved={(server) => {
            setFound(null);
            setAdmitted(server);
            afterChange();
          }}
        />
      )}

      <Card
        title="Waiting to be approved"
        description="Machines that have asked to join in the last fifteen minutes. Enter a machine's own code above to admit it, or deny one you do not recognise."
      >
        {waiting.isPending && <Loading />}

        {waiting.isError && (
          <Alert tone="danger" title="Could not load the pairing requests">
            Try again in a moment.
          </Alert>
        )}

        {waiting.data?.items.length === 0 && (
          <EmptyState title="Nothing is waiting">
            A request appears here for {PAIRING_TTL_MINUTES} minutes after somebody runs{' '}
            <code>bartizo-agent pair</code> on a server.
          </EmptyState>
        )}

        {waiting.data !== undefined && waiting.data.items.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <caption className="visually-hidden">Servers waiting to be approved</caption>
              <thead>
                <tr>
                  <th scope="col">Hostname</th>
                  <th scope="col">Address</th>
                  <th scope="col">Key fingerprint</th>
                  <th scope="col">Asked</th>
                  <th scope="col">Expires</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {waiting.data.items.map((request) => (
                  <WaitingRow
                    key={request.id}
                    request={request}
                    timezone={timezone}
                    onDenied={afterChange}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * One waiting machine, with the refusal P2.4 left out (P8.4): an admin who does not
 * recognise a hostname tells it no rather than waiting for its code to run out.
 * The agent hears "denied" and stops, and the request cannot be approved after.
 */
function WaitingRow({
  request,
  timezone,
  onDenied,
}: {
  request: PairingRequestSummary;
  timezone: string;
  onDenied: () => void;
}): ReactNode {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deny = useMutation({
    mutationFn: () => denyPairingRequest(request.id),
    onSuccess: () => {
      setConfirming(false);
      onDenied();
    },
    onError: (caught: unknown) => {
      setError(caught instanceof Error ? caught.message : 'Could not deny it.');
    },
  });

  return (
    <tr>
      <th scope="row">{request.hostname}</th>
      <td className="text-sm">{request.ip ?? <span className="muted">—</span>}</td>
      <td>
        <code className="text-sm">{request.fingerprint}</code>
      </td>
      <td className="text-sm">{formatRelative(request.requestedAt)}</td>
      <td className="text-sm">{formatInstant(request.expiresAt, { timezone })}</td>
      <td>
        <Button
          variant="ghost"
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
        >
          Deny
        </Button>
        {confirming && (
          <ConfirmDialog
            title={`Deny ${request.hostname}?`}
            description="The machine is told it was refused and stops waiting. Whoever runs it can ask again with a new code; the refusal is in the audit log."
            confirmLabel="Deny it"
            danger
            busy={deny.isPending}
            error={error}
            onConfirm={() => {
              deny.mutate();
            }}
            onCancel={() => {
              setConfirming(false);
            }}
          />
        )}
      </td>
    </tr>
  );
}

function EnterCodeCard({ onFound }: { onFound: (found: Found) => void }): ReactNode {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const look = useMutation({
    mutationFn: (typed: string) => lookupPairingCode(typed),
    onSuccess: (request) => {
      onFound({ request, code });
    },
    onError: (caught: unknown) => {
      setError(
        isApiError(caught) && caught.code === 'pairing_not_found'
          ? 'No machine is waiting with that code. Check it against the terminal, or run bartizo-agent pair again on the server for a new one.'
          : caught instanceof Error
            ? caught.message
            : 'Could not look that code up.',
      );
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    setError(null);
    look.mutate(code.trim());
  };

  return (
    <Card
      title="Enter the pairing code"
      description="The eight characters the server printed in its terminal."
    >
      <form onSubmit={submit} className="stack">
        <TextField
          label="Pairing code"
          hint="Upper or lower case, with or without the hyphen."
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
          // Nothing about a one-time code belongs in a password manager, a spell
          // checker or an autofill history.
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoCapitalize="characters"
          placeholder="ABCD-EFGH"
          error={error}
        />

        <div className="row">
          <Button
            type="submit"
            variant="primary"
            busy={look.isPending}
            busyLabel="Looking…"
            disabled={code.trim().length === 0}
          >
            Look it up
          </Button>
        </div>
      </form>
    </Card>
  );
}

function CheckAndApproveCard({
  found,
  timezone,
  onApproved,
  onCancel,
}: {
  found: Found;
  timezone: string;
  onApproved: (server: ServerDetail) => void;
  onCancel: () => void;
}): ReactNode {
  const { request } = found;
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });
  const [groupId, setGroupId] = useState(NO_GROUP);
  const [error, setError] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: () =>
      approvePairingRequest(request.id, {
        code: found.code,
        groupId: groupId === NO_GROUP ? null : groupId,
      }),
    onSuccess: (response) => {
      onApproved(response.server);
    },
    onError: (caught: unknown) => {
      setError(caught instanceof Error ? caught.message : 'Could not approve it.');
    },
  });

  if (request.state !== 'pending') {
    return (
      <Card title={request.hostname} aside={<span className="badge">{request.state}</span>}>
        <Alert tone="warning" title="There is nothing to approve">
          {request.state === 'expired' ? (
            <>
              That code ran out. Run <code>bartizo-agent pair</code> on the server for a new one.
            </>
          ) : request.state === 'denied' ? (
            'That request was denied, and cannot be approved now. The machine can ask again with a new code.'
          ) : (
            'That request has already been approved. The server should be in the fleet already.'
          )}
        </Alert>
        <div className="row">
          <Button onClick={onCancel}>Close</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Check these against the terminal"
      description="All four are printed on the server itself. If any of them disagrees, do not approve."
      aside={<span className="badge badge-accent">{formatPairingCode(found.code)}</span>}
    >
      <dl className="details">
        <dt>Hostname</dt>
        <dd>{request.hostname}</dd>

        <dt>Key fingerprint</dt>
        <dd>
          <code className="secret-text">{request.fingerprint}</code>
        </dd>

        <dt>Address it reported</dt>
        <dd>{request.ip ?? <span className="muted">—</span>}</dd>

        <dt>Address it came from</dt>
        <dd>
          {request.peerIp ?? <span className="muted">—</span>}
          {request.ip !== null && request.peerIp !== null && request.ip !== request.peerIp && (
            <span className="muted text-sm"> — differs, which is normal behind NAT</span>
          )}
        </dd>

        <dt>Operating system</dt>
        <dd>{request.os ?? <span className="muted">—</span>}</dd>

        <dt>Detected roles</dt>
        <dd>{request.detectedRoles.length > 0 ? request.detectedRoles.join(', ') : '—'}</dd>

        <dt>Agent version</dt>
        <dd>{request.agentVersion ?? <span className="muted">—</span>}</dd>

        <dt>Code expires</dt>
        <dd>{formatInstant(request.expiresAt, { timezone })}</dd>
      </dl>

      <SelectField
        label="Group"
        hint="An enrollment token carries this; a pairing code has nobody to carry it but you."
        value={groupId}
        onChange={(event) => {
          setGroupId(event.target.value);
        }}
      >
        <option value={NO_GROUP}>No group</option>
        {(groups.data ?? []).map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </SelectField>

      {error !== null && <Alert tone="danger">{error}</Alert>}

      <div className="row">
        <Button
          variant="primary"
          busy={approve.isPending}
          busyLabel="Approving…"
          onClick={() => {
            setError(null);
            approve.mutate();
          }}
        >
          Approve and admit this server
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}
